/**
 * Settings page - ledger description prefixes, PDF config, variance
 * thresholds, and network/runtime preferences.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, LifeBuoy, LockKeyhole, Users2, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useWhoami } from '../lib/auth';
import {
  UI_FONT_SCALE_OPTIONS,
  useUiPreferences,
  type UiFontScale,
} from '../lib/uiPreferences';
import { AppShell, useReadOnly } from '../ui/AppShell';
import { SectionCard } from '../ui/components';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import { PageHeader } from '../ui/PageHeader';
import {
  Button,
  ErrorBanner,
  Pill,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { weekLabelForDebug } from '../lib/week';

export function SettingsPage() {
  const readOnly = useReadOnly();
  const who = useWhoami();
  const role = who.data?.state === 'authenticated' ? who.data.role : null;
  const isOwner = role === 'owner';

  return (
    <AppShell
      title="Settings"
      toolbar={
        <PageHeader
          section="settings"
          title="Settings"
          subtitle="Display size, statements, exports, and runtime preferences."
        />
      }
    >
      <div className="space-y-4">
        <MetricStrip
          section="settings"
          items={[
            { label: 'Role', value: role === 'owner' ? 'Owner' : 'Boss', note: 'current session' },
            {
              label: 'Mode',
              value: readOnly ? 'View only' : 'Editable',
              note: readOnly ? 'shared settings are locked' : 'shared changes save immediately',
            },
            { label: 'Exports', value: 'PDF + PNG', note: 'statement output' },
          ]}
        />

        <div className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
          <ActionQueueCard
            section="settings"
            eyebrow="Control room"
            title={readOnly ? 'Boss mode can review shared settings' : 'Owner mode can change app behavior'}
            description={
              readOnly
                ? 'Statement wording, export quality, thresholds, and runtime choices stay visible here. Your own reading size on this device can still be changed below.'
                : 'Use this desk for the small system-level choices that shape statements, variance review, and how the app binds to the network.'
            }
            tone={readOnly ? 'neutral' : 'accent'}
            meta={
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span>Who can edit</span>
                  <span className="font-medium text-ink">{readOnly ? 'Owner only' : 'This session'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Owner-only desks</span>
                  <span className="font-medium text-ink">Users and repair</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Reading size</span>
                  <span className="font-medium text-ink">Per device</span>
                </div>
                <div className="text-[12px] leading-5 text-ink-muted">
                  Keep copy and threshold changes conservative. These settings feed live statements and settlement review immediately after save.
                </div>
              </div>
            }
          />

          <div className="space-y-4">
            <SectionCard section="settings" title="Quick destinations">
              <div className="grid gap-3 sm:grid-cols-2">
                <SettingsLinkCard
                  to="/account"
                  icon={LockKeyhole}
                  title="My account"
                  description="Password, PIN, and sign-out controls."
                />
                {isOwner && (
                  <SettingsLinkCard
                    to="/settings/users"
                    icon={Users2}
                    title="Users"
                    description="Create, reset, and remove boss users."
                  />
                )}
                {isOwner && (
                  <SettingsLinkCard
                    to="/fix-problems"
                    icon={Wrench}
                    title="Fix problems"
                    description="Merge duplicates and move wrong-person history — safely, with undo."
                  />
                )}
                {isOwner && (
                  <SettingsLinkCard
                    to="/repair"
                    icon={LifeBuoy}
                    title="Repair"
                    description="Run diagnostics and generate a support bundle."
                  />
                )}
              </div>
            </SectionCard>
            <ReadingSizeCard />
          </div>
        </div>

        {readOnly && (
          <ActionQueueCard
            section="settings"
            eyebrow="Read only"
            title="Shared settings are blocked in boss mode"
            description="That includes statement wording, export quality, variance thresholds, network bind, users, and repair actions. Reading size on this device still updates instantly."
            tone="neutral"
          />
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <LedgerDescriptionCard readOnly={readOnly} />
          <PdfConfigCard readOnly={readOnly} />
          <VarianceThresholdsCard readOnly={readOnly} />
          <NetworkBindCard readOnly={readOnly} />
        </div>
      </div>
    </AppShell>
  );
}

function ReadingSizeCard() {
  const { fontScale, setFontScale } = useUiPreferences();

  return (
    <SectionCard
      section="settings"
      title="Reading size"
      action={<Pill section="settings">This device only</Pill>}
    >
      <p className="mb-4 text-[13px] leading-5 text-ink-muted">
        Make useful text larger across every screen in this browser without changing shared data, exported statements, or anyone else&apos;s view.
      </p>
      <div className="grid gap-2.5 sm:grid-cols-3">
        {UI_FONT_SCALE_OPTIONS.map((option) => (
          <ReadingSizeOption
            key={option.value}
            option={option}
            active={option.value === fontScale}
            onSelect={setFontScale}
          />
        ))}
      </div>
      <div className="mt-4 app-shell-surface-soft rounded-[22px] border border-border/80 p-4 shadow-sm backdrop-blur">
        <div className="text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted">
          Preview
        </div>
        <div className="mt-2 text-h2 font-semibold tracking-[-0.02em] text-ink">
          Weekly batches
        </div>
        <div className="mt-1 text-body text-ink-muted">
          Important actions stay prominent while helper notes remain quieter.
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-full bg-[var(--sec-settings-tint)] px-3 py-1.5 text-small font-semibold text-[var(--sec-settings)]">
            Ready to post
          </span>
          <span className="text-caption text-ink-muted">Applies immediately across all pages.</span>
        </div>
      </div>
    </SectionCard>
  );
}

function ReadingSizeOption({
  option,
  active,
  onSelect,
}: {
  option: { value: UiFontScale; label: string; note: string };
  active: boolean;
  onSelect: (value: UiFontScale) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onSelect(option.value)}
      className={[
        'rounded-[20px] border px-3.5 py-3 text-left shadow-sm backdrop-blur transition-all duration-150',
        active
          ? 'text-ink'
          : 'app-shell-surface hover:-translate-y-px hover:bg-surface-raised',
      ].join(' ')}
      style={
        active
          ? {
              borderColor: 'var(--sec-settings)',
              background:
                'linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, color-mix(in srgb, var(--sec-settings-tint) 78%, white 22%) 100%)',
              boxShadow: '0 20px 34px -26px rgba(96, 118, 137, 0.48)',
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-small font-semibold text-ink">{option.label}</div>
          <div className="mt-1 text-caption leading-5 text-ink-muted">{option.note}</div>
        </div>
        <span
          className="mt-0.5 h-3 w-3 shrink-0 rounded-full border"
          style={{
            borderColor: active ? 'var(--sec-settings)' : 'var(--border-strong)',
            background: active ? 'var(--sec-settings)' : 'transparent',
          }}
          aria-hidden
        />
      </div>
    </button>
  );
}

function SettingsLinkCard({
  to,
  icon: Icon,
  title,
  description,
  className,
}: {
  to: string;
  icon: typeof LockKeyhole;
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={[
        'group flex min-h-[104px] items-start gap-3 rounded-[24px] border border-border/70 app-shell-surface px-4 py-4 shadow-sm backdrop-blur transition-all duration-150 hover:-translate-y-px hover:bg-surface-raised',
        className ?? '',
      ].join(' ')}
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[18px] bg-[var(--sec-settings-tint)] text-[var(--sec-settings)]">
        <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-small font-semibold tracking-[-0.01em] text-ink">{title}</div>
        <div className="mt-1 text-[12px] leading-5 text-ink-muted">{description}</div>
      </div>
      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
    </Link>
  );
}

// -- Ledger description prefix -----------------------------------------------

type KVSetting<T> = { key: string; defaultValue: T };
async function getRawSetting<T>(s: KVSetting<T>): Promise<T> {
  void s;
  throw new Error('unused');
}
void getRawSetting;

function LedgerDescriptionCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const [prefix, setPrefix] = useState('');
  const [paymentPrefix, setPaymentPrefix] = useState('');
  const [error, setError] = useState<string | null>(null);

  const settings = useQuery<{ prefix: string; paymentPrefix: string }>({
    queryKey: ['settings', 'ledger'],
    queryFn: () => api.get('/api/settings/ledger'),
  });
  useEffect(() => {
    if (settings.data) {
      setPrefix(settings.data.prefix);
      setPaymentPrefix(settings.data.paymentPrefix);
    }
  }, [settings.data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await api.put('/api/settings/ledger', { prefix, paymentPrefix });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings', 'ledger'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const sampleWeek = weekLabelForDebug('2026-04-06');
  const sampleDesc = `${prefix}LOTUS365DA ${sampleWeek}`;
  const samplePaymentDesc = paymentPrefix.replaceAll('{payer}', 'SRB').concat('C0F999 VICKY');

  return (
    <SectionCard section="settings" title="Statement wording">
      <p className="mb-4 text-[13px] leading-5 text-ink-muted">
        These prefixes shape how weekly P&amp;L rows and payment descriptions appear in ledger statements.
      </p>
      {settings.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading...
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (readOnly) return;
            setError(null);
            saveMut.mutate();
          }}
        >
          <TextInput
            label="Weekly P&L prefix"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            hint={`Preview: ${sampleDesc}`}
            readOnly={readOnly}
          />
          <TextInput
            label="Payment description template"
            value={paymentPrefix}
            onChange={(e) => setPaymentPrefix(e.target.value)}
            hint={`Preview: ${samplePaymentDesc}`}
            readOnly={readOnly}
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!readOnly && (
            <div>
              <Button type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : 'Save wording'}
              </Button>
            </div>
          )}
        </form>
      )}
    </SectionCard>
  );
}

// -- PDF config ---------------------------------------------------------------

function PdfConfigCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const settings = useQuery<{ rowsPerPage: number; deviceScaleFactor: number }>({
    queryKey: ['settings', 'pdf'],
    queryFn: () => api.get('/api/settings/pdf'),
  });
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [deviceScaleFactor, setDeviceScaleFactor] = useState(2);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setRowsPerPage(settings.data.rowsPerPage);
      setDeviceScaleFactor(settings.data.deviceScaleFactor);
    }
  }, [settings.data]);

  const saveMut = useMutation({
    mutationFn: async () => api.put('/api/settings/pdf', { rowsPerPage, deviceScaleFactor }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings', 'pdf'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <SectionCard section="settings" title="Export quality">
      <p className="mb-4 text-[13px] leading-5 text-ink-muted">
        Tune how many rows fit on a page and how sharp PNG snapshots render when someone saves or shares a statement.
      </p>
      {settings.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading...
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (readOnly) return;
            setError(null);
            saveMut.mutate();
          }}
        >
          <TextInput
            label="Rows per page"
            type="number"
            min={5}
            max={200}
            value={String(rowsPerPage)}
            onChange={(e) => setRowsPerPage(Math.max(5, Math.min(200, Number(e.target.value) || 40)))}
            hint="Default 40. Range 5-200."
            readOnly={readOnly}
          />
          <TextInput
            label="PNG sharpness"
            type="number"
            min={1}
            max={4}
            step={0.5}
            value={String(deviceScaleFactor)}
            onChange={(e) => setDeviceScaleFactor(Math.max(1, Math.min(4, Number(e.target.value) || 2)))}
            hint="Default 2. Higher values create sharper, larger files."
            readOnly={readOnly}
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!readOnly && (
            <div>
              <Button type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : 'Save export settings'}
              </Button>
            </div>
          )}
        </form>
      )}
    </SectionCard>
  );
}

// -- Variance thresholds ------------------------------------------------------

function VarianceThresholdsCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const settings = useQuery<{ minor: string; review: string }>({
    queryKey: ['settings', 'variance'],
    queryFn: () => api.get('/api/settings/variance'),
  });
  const [minor, setMinor] = useState('');
  const [review, setReview] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setMinor(settings.data.minor);
      setReview(settings.data.review);
    }
  }, [settings.data]);

  const saveMut = useMutation({
    mutationFn: () => api.put('/api/settings/variance', { minor, review }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings', 'variance'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <SectionCard section="settings" title="Variance review bands">
      <p className="mb-4 text-[13px] leading-5 text-ink-muted">
        These thresholds classify the difference between the imported amount and the recomputed settlement amount during batch compute.
      </p>
      {settings.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading...
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (readOnly) return;
            setError(null);
            saveMut.mutate();
          }}
        >
          <TextInput
            label="Minor up to"
            value={minor}
            onChange={(e) => setMinor(e.target.value)}
            hint="Below this is treated as cosmetic rounding."
            readOnly={readOnly}
          />
          <TextInput
            label="Review up to"
            value={review}
            onChange={(e) => setReview(e.target.value)}
            hint="Above minor, up to this value is surfaced for review."
            readOnly={readOnly}
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!readOnly && (
            <div>
              <Button type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : 'Save thresholds'}
              </Button>
            </div>
          )}
        </form>
      )}
    </SectionCard>
  );
}

// -- Network bind -------------------------------------------------------------

function NetworkBindCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const settings = useQuery<{ bind: string; port: number }>({
    queryKey: ['settings', 'network'],
    queryFn: () => api.get('/api/settings/network'),
  });
  const [bind, setBind] = useState('');
  const [port, setPort] = useState(4400);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setBind(settings.data.bind);
      setPort(settings.data.port);
    }
  }, [settings.data]);

  const saveMut = useMutation({
    mutationFn: () => api.put('/api/settings/network', { bind, port }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings', 'network'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <SectionCard section="settings" title="Network bind">
      <p className="mb-4 text-[13px] leading-5 text-warn">
        This decides where the backend listens. Changing it can expose the app beyond the current machine or server and needs a restart before it takes effect.
      </p>
      {settings.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading...
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (readOnly) return;
            setError(null);
            saveMut.mutate();
          }}
        >
          <TextInput
            label="Bind address"
            value={bind}
            onChange={(e) => setBind(e.target.value)}
            readOnly={readOnly}
          />
          <TextInput
            label="Port"
            type="number"
            value={String(port)}
            onChange={(e) => setPort(Math.max(1, Math.min(65535, Number(e.target.value) || 4400)))}
            readOnly={readOnly}
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!readOnly && (
            <div>
              <Button type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : 'Save network settings'}
              </Button>
            </div>
          )}
        </form>
      )}
    </SectionCard>
  );
}
